import React from 'react';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormFooter, FormHeader, FormSection, Field, FormApi, ErrorMessage } from '@atlaskit/form';
import get from 'lodash/get';
import set from 'lodash/set';
import Textfield from '@atlaskit/textfield';
import {
  InfoPanel,
  RequiredFieldMessage,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  ErrorComponents,
} from '@css-xp/base';
import { AssignReservedCloudNameRequest } from '@atlassian/cssxp-typescommon';
import { unAssignReservedCloudSiteName } from '@atlassian/temp-proxies';

interface Props {
  cloudSiteId?: string;
  cloudSiteName?: string;
}

interface FormData {
  cloudName?: string;
  companyDomain?: string;
  companyName?: string;
  ticketUrl?: string;
}
interface ReserveNewCloudSiteRequest {
  cloudName: string;
  companyDomain?: string;
  companyName?: string;
  ticketUrl: string;
}
interface UnAssignReservedCloudNameRequest extends AssignReservedCloudNameRequest {
  ARI: string;
  cloudSiteId: string;
}

const UnAssignReservedSiteNamePane = ({ cloudSiteId = '', cloudSiteName = '' }: Props) => {
  const { closeSidebar } = useSidebar();
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UnAssignReservedCloudNameRequest, any>(
    {
      submitFormAction: unAssignReservedCloudSiteName,
      onComplete: () => {
        closeSidebar();
      },
    }
  );

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('un-assign-reserved-site-success')
      .withHeader('Un-assign reserved site name')
      .withMessage('Reserved cloud name successfully un-assigned')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const constructRequestData = (formData: FormData, formApi: FormApi<FormData>) => {
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(formData, key);
      set(transformedData, key, value);
    });

    const requestData: UnAssignReservedCloudNameRequest = {
      ARI: cloudSiteId ? `ari:cloud:platform::site/${cloudSiteId}` : '',
      cloudName: cloudSiteName ?? '',
      ticketUrl: transformedData?.ticketUrl ?? '',
      cloudSiteId,
    };

    return requestData;
  };

  const handleFormSubmit = (formData: FormData, formApi: FormApi<FormData>) => {
    const requestData = constructRequestData(formData, formApi);
    submitForm(requestData);
  };
  return (
    <div style={{ maxWidth: '500px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title="Un-assign reserved site name"></FormHeader>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to un-assign this cloud name for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <Field name="cloudName" label="Reserved cloud name" isRequired={true} isDisabled>
                {({ fieldProps }) => <Textfield {...fieldProps} name="cloudName" value={cloudSiteName} />}
              </Field>
              <Field name="cloudSite" label="Cloud site" isRequired={true} isDisabled>
                {({ fieldProps }) => <Textfield {...fieldProps} name="cloudSite" value={cloudSiteId} />}
              </Field>
              <Field name="ticketUrl" label="Ticket URL" isRequired={true}>
                {({ fieldProps }) => <Textfield {...fieldProps} name="ticketUrl" />}
              </Field>
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={isProcessing}
                  testId="un-assign-site-name-btn"
                >
                  Un-assign
                </LoadingButton>
                <Button appearance="subtle" onClick={() => closeSidebar()} testId="cancel">
                  Cancel
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default UnAssignReservedSiteNamePane;
